import { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Quote, X } from 'lucide-react';

// Convert a YouTube / Vimeo watch URL to an embeddable player URL.
export const toEmbed = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  return null;
};

export const VideoGrid = ({ videos, testid = 'rich-videos' }) => {
  const rows = (videos || []).filter((v) => v && v.url);
  if (!rows.length) return null;
  return (
    <section data-testid={testid}>
      <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4">Videos</h2>
      <div className="grid sm:grid-cols-2 gap-5">
        {rows.map((v, i) => {
          const embed = toEmbed(v.url);
          return (
            <div key={i} data-testid={`${testid}-${i}`}>
              <div className="aspect-video rounded-lg overflow-hidden border border-[#E5E7EB] bg-black">
                {embed
                  ? <iframe src={embed} title={v.title || `Video ${i + 1}`} className="w-full h-full" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  : <a href={v.url} target="_blank" rel="noreferrer" className="w-full h-full flex items-center justify-center text-white text-sm underline">Watch video</a>}
              </div>
              {v.title && <p className="text-sm text-gray-600 mt-1.5">{v.title}</p>}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export const ImageGallery = ({ images, title = 'Gallery', testid = 'rich-gallery' }) => {
  const rows = (images || []).filter(Boolean);
  const [active, setActive] = useState(null);
  if (!rows.length) return null;
  return (
    <section data-testid={testid}>
      <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4">{title}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {rows.map((src, i) => (
          <button key={i} type="button" onClick={() => setActive(src)} data-testid={`${testid}-item-${i}`}
            className="block rounded-lg overflow-hidden border border-[#E5E7EB] bg-white hover:border-[#00A79D] transition-colors">
            <img src={src} alt={`${title} ${i + 1}`} className="w-full h-40 object-cover" loading="lazy" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
          </button>
        ))}
      </div>
      {active && (
        <div className="fixed inset-0 z-[120] bg-black/80 flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid={`${testid}-lightbox`}>
          <button type="button" className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setActive(null)}><X className="w-7 h-7" /></button>
          <img src={active} alt="Preview" className="max-h-[90vh] max-w-full rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </section>
  );
};

export const AccordionList = ({ items, title = 'More information', testid = 'rich-accordions' }) => {
  const rows = (items || []).filter((a) => a && (a.heading || a.body));
  if (!rows.length) return null;
  return (
    <section data-testid={testid}>
      <h2 className="font-heading text-xl font-semibold text-gray-900 mb-3">{title}</h2>
      <div className="space-y-2">
        {rows.map((a, i) => (
          <details key={i} className="group border border-[#E5E7EB] rounded-lg bg-white" data-testid={`${testid}-${i}`}>
            <summary className="cursor-pointer list-none px-4 py-3 font-semibold text-gray-900 flex items-center justify-between">
              {a.heading}<span className="text-[#00A79D] text-lg group-open:rotate-45 transition-transform">+</span>
            </summary>
            <div className="px-4 pb-4 text-sm text-gray-600 whitespace-pre-wrap">{a.body}</div>
          </details>
        ))}
      </div>
    </section>
  );
};

export const TestimonialCarousel = ({ items, testid = 'rich-testimonials' }) => {
  const rows = (items || []).filter((t) => t && t.quote);
  const scroller = useRef(null);
  const [idx, setIdx] = useState(0);
  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    const w = el.clientWidth || 1;
    setIdx(Math.round(el.scrollLeft / w));
  };
  const go = (i) => {
    const el = scroller.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(rows.length - 1, i));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: 'smooth' });
  };
  useEffect(() => { setIdx(0); }, [rows.length]);
  if (!rows.length) return null;
  return (
    <section data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-semibold text-gray-900">What customers say</h2>
        {rows.length > 1 && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => go(idx - 1)} disabled={idx === 0} data-testid={`${testid}-prev`}
              className="w-9 h-9 rounded-full border border-[#E5E7EB] flex items-center justify-center text-gray-500 hover:text-[#00857D] hover:border-[#00A79D] disabled:opacity-40 disabled:hover:text-gray-500 transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button type="button" onClick={() => go(idx + 1)} disabled={idx === rows.length - 1} data-testid={`${testid}-next`}
              className="w-9 h-9 rounded-full border border-[#E5E7EB] flex items-center justify-center text-gray-500 hover:text-[#00857D] hover:border-[#00A79D] disabled:opacity-40 disabled:hover:text-gray-500 transition-colors">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
      <div ref={scroller} onScroll={onScroll}
        className="flex overflow-x-auto snap-x snap-mandatory gap-4 scrollbar-hide -mx-1 px-1"
        style={{ scrollbarWidth: 'none' }}>
        {rows.map((t, i) => (
          <blockquote key={i} data-testid={`${testid}-${i}`}
            className="snap-center shrink-0 w-full bg-white border border-[#E5E7EB] rounded-xl p-6 md:p-8">
            <Quote className="w-7 h-7 text-[#00A79D]/40 mb-3" />
            <p className="text-lg text-gray-800 italic leading-relaxed">"{t.quote}"</p>
            {(t.author || t.role) && (
              <footer className="mt-4 text-sm text-gray-500">
                {t.author && <span className="font-semibold text-gray-700">{t.author}</span>}
                {t.author && t.role && <span> · </span>}
                {t.role && <span>{t.role}</span>}
              </footer>
            )}
          </blockquote>
        ))}
      </div>
      {rows.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-4">
          {rows.map((_, i) => (
            <button key={i} type="button" onClick={() => go(i)} aria-label={`Go to testimonial ${i + 1}`}
              className={`h-2 rounded-full transition-all ${i === idx ? 'w-6 bg-[#00A79D]' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} />
          ))}
        </div>
      )}
    </section>
  );
};
